/**
 * useCancelBooking Hook
 * 
 * React Query mutation hook for canceling bookings.
 * Handles automatic refunds based on payment type.
 * 
 * @module booking/hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { bookingService } from '../api/bookingService';
import type { ApiResult } from '~/lib/apiClient/types';
import type { ApiCancelBookingPayload } from '@lgc/database/types/domains/booking';

/**
 * Options for useCancelBooking hook
 */
export interface UseCancelBookingOptions {
  /**
   * Club ID for the booking (required for query invalidation)
   */
  clubId: number;

  /**
   * Callback invoked on successful cancellation
   * @param data - Cancellation response (`creditedAmountCents` = money converted to credit)
   * @param bookingId - ID of cancelled booking
   */
  onSuccess?: (data: ApiCancelBookingPayload, bookingId: number) => void;

  /**
   * Callback invoked on cancellation error
   * @param error - Error object from API
   * @param bookingId - ID of booking that failed to cancel
   */
  onError?: (error: Error, bookingId: number) => void;

  /**
   * Whether to automatically invalidate booking queries after cancellation
   * @default true
   */
  invalidateQueries?: boolean;
}

/**
 * Cancel Booking Mutation Hook
 * 
 * Provides mutation function to cancel a booking with automatic refund processing.
 * 
 * **Cancel Behavior:**
 * - Pass bookings: minutes restored automatically ✅
 * - Membership bookings: no money involved ✅
 * - Direct / hybrid / cross-club (real money): amount converted to ACCOUNT CREDIT
 *   (`creditedAmountCents`); the card is never touched ✅
 * - Expired-pass bookings: still route to the club (`EXPIRED_PASS_CANCELLATION`) ❌
 * - Lesson bookings: pack minutes restored; the packages view is invalidated
 *
 * **Error Handling:**
 * ```typescript
 * const { mutate, error } = useCancelBooking({
 *   clubId: 2,
 *   onError: (error) => {
 *     if (error.message.includes('contact')) {
 *       // Show "contact club" message to user
 *     }
 *   }
 * });
 * ```
 * 
 * @param options - Configuration options
 * @returns React Query mutation object
 * 
 * @example
 * ```typescript
 * const { mutate: cancelBooking, isPending } = useCancelBooking({
 *   clubId: 2,
 *   onSuccess: () => {
 *     butterToast.success('Booking cancelled successfully');
 *     navigate('/');
 *   },
 *   onError: (error) => {
 *     if (error.message.includes('contact')) {
 *       butterToast.error('Please contact the club to cancel this booking');
 *     } else {
 *       butterToast.error('Failed to cancel booking');
 *     }
 *   }
 * });
 * 
 * // Cancel booking
 * cancelBooking(184);
 * ```
 */
export function useCancelBooking(options: UseCancelBookingOptions) {
  const queryClient = useQueryClient();
  const { clubId, onSuccess, onError, invalidateQueries = true } = options;

  return useMutation({
    mutationFn: async (bookingId: number) => {
      const result: ApiResult<ApiCancelBookingPayload> =
        await bookingService.cancelBooking(bookingId);

      if (!result.success) {
        // Carry the server's structured reason + contactRequired flag so consumers
        // branch on them instead of substring-matching the message (transient 500s also
        // say "…contact your club"). `contactRequired` marks the permanent contact-the-club
        // cases (expired pass, no-home-club); `reason` is kept for finer-grained handling.
        const err = new Error(result.message || 'Failed to cancel booking') as Error & { reason?: string; contactRequired?: boolean };
        const details = result.error?.details as { reason?: string; contactRequired?: boolean } | undefined;
        err.reason = details?.reason;
        err.contactRequired = details?.contactRequired;
        throw err;
      }

      return result.payload;
    },

    onSuccess: (data, bookingId) => {
      // Invalidate booking queries using centralized query keys
      if (invalidateQueries) {
        // Invalidate all bookings for this club
        queryClient.invalidateQueries({ 
          queryKey: queryKeys.bookings.all(clubId)
        });

        // Invalidate specific booking detail cache.
        // detail() is not club-scoped (booking IDs are globally unique).
        queryClient.invalidateQueries({
          queryKey: queryKeys.bookings.detail(String(bookingId))
        });
        
        // Invalidate bay availability since a slot opened up
        // Note: API only returns { bookingId }, so we can't target specific bay/date
        // Therefore, we invalidate all bay availability for this club
        queryClient.invalidateQueries({
          queryKey: queryKeys.bays.all(clubId),
          refetchType: 'active',
        });

        // My Lessons must show the restored balance without a manual refresh.
        queryClient.invalidateQueries({
          queryKey: queryKeys.lessons.all(clubId),
        });
      }

      // Call user-provided success callback
      if (onSuccess) {
        onSuccess(data, bookingId);
      }
    },

    onError: (error: Error, bookingId) => {
      console.error('[useCancelBooking] Cancellation failed:', {
        bookingId,
        error: error.message,
      });

      // Call user-provided error callback
      if (onError) {
        onError(error, bookingId);
      }
    },
  });
}
